import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import io from 'socket.io-client';

const TEAM_INFO = {
  red: { name: 'Tokyo', color: '#ff4444', piece: '🌸' },
  blue: { name: 'Vancouver', color: '#4488ff', piece: '🏔️' },
  green: { name: 'New York', color: '#44cc44', piece: '🗽' },
  yellow: { name: 'London', color: '#ffcc00', piece: '🕰️' },
};

const BattleshipPlayPage = () => {
  const [board, setBoard] = useState(null);
  const [myTeam, setMyTeam] = useState(null);
  const [myVote, setMyVote] = useState(null);
  const [voteResult, setVoteResult] = useState(null); // { correct, choice, answer }
  const [isShooter, setIsShooter] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [battleshipState, setBattleshipState] = useState(null);
  const socketRef = useRef(null);
  const timerRef = useRef(null);
  const userIdRef = useRef(null);

  const fetchBoard = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/monopoly/state`);
      const data = await res.json();
      if (data.board) setBoard(data.board);
    } catch (err) { /* ignore */ }
  }, []);

  const fetchBattleship = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/battleship/spectate`);
      const data = await res.json();
      if (data.game) setBattleshipState(data.game);
    } catch (err) { /* ignore */ }
  }, []);

  useEffect(() => {
    // Get user info from localStorage
    const userData = localStorage.getItem('userData');
    if (userData) {
      try {
        const parsed = JSON.parse(userData);
        userIdRef.current = parsed.id || parsed.userId;
      } catch (e) { /* ignore */ }
    }

    fetchBoard();
    fetchBattleship();

    const token = localStorage.getItem('token');
    const socket = io(API_BASE_URL, {
      auth: token ? { token } : { public: true }
    });
    socketRef.current = socket;

    // Get team assignment
    socket.on('connect', () => {
      // Team info comes from board state or we fetch it
      fetchTeam();
    });

    socket.on('monopoly:state', (data) => setBoard(data));
    socket.on('monopoly:advance', (data) => {
      setBoard(data.board);
      setMyVote(null);
      setVoteResult(null);
      setIsShooter(false);
    });
    socket.on('monopoly:voting-start', (data) => {
      fetchBoard();
      setMyVote(null);
      setVoteResult(null);
      if (data.endsAt) startCountdown(data.endsAt);
    });
    socket.on('monopoly:vote-count', () => fetchBoard());
    socket.on('monopoly:voting-end', () => {
      setTimeLeft(null);
      fetchBoard();
    });
    socket.on('monopoly:reveal', (data) => {
      setBoard(data.board);
      // Check my result
      if (userIdRef.current && data.userResults?.[userIdRef.current]) {
        setVoteResult(data.userResults[userIdRef.current]);
      }
    });
    socket.on('monopoly:shooters', (data) => {
      setBoard(data.board);
      // Check if I'm a shooter
      if (userIdRef.current && data.shooters) {
        for (const [team, shooterIds] of Object.entries(data.shooters)) {
          if (shooterIds.includes(userIdRef.current)) {
            setIsShooter(true);
            break;
          }
        }
      }
    });
    socket.on('monopoly:reset', () => {
      setBoard(null);
      setMyVote(null);
      setVoteResult(null);
      setIsShooter(false);
    });

    socket.on('monopoly:vote-accepted', (data) => {
      setMyVote(data.choice);
      setMyTeam(data.team);
    });
    socket.on('monopoly:vote-error', (data) => {
      console.error('Vote error:', data.error);
    });

    // Battleship updates
    socket.on('battleship:hit', () => fetchBattleship());
    socket.on('battleship:miss', () => fetchBattleship());
    socket.on('battleship:sunk', () => fetchBattleship());
    socket.on('battleship:game-start', () => fetchBattleship());

    const poll = setInterval(() => { fetchBoard(); fetchBattleship(); }, 5000);

    return () => {
      socket.disconnect();
      clearInterval(poll);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [fetchBoard, fetchBattleship]);

  const fetchTeam = async () => {
    try {
      if (!userIdRef.current) return;
      const res = await fetch(`${API_BASE_URL}/api/user/${userIdRef.current}/teams`);
      if (res.ok) {
        const data = await res.json();
        const btlTeam = data.teams?.battleship;
        if (btlTeam) {
          const teamNames = ['red', 'blue', 'green', 'yellow'];
          setMyTeam(teamNames[btlTeam.team] || null);
        }
      }
    } catch (e) { /* ignore */ }
  };

  const startCountdown = (endsAt) => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((endsAt - Date.now()) / 1000));
      setTimeLeft(remaining);
      if (remaining <= 0) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }, 100);
  };

  const handleVote = (choice) => {
    if (myVote || !socketRef.current) return;
    socketRef.current.emit('monopoly:vote', { choice });
  };

  const currentSquare = board?.currentPosition >= 0 ? board.squares[board.currentPosition] : null;
  const teamInfo = myTeam ? TEAM_INFO[myTeam] : null;

  return (
    <div style={{ padding: '16px', maxWidth: '500px', margin: '0 auto', minHeight: '80vh' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '16px' }}>
        <h2 style={{ color: '#4488ff', margin: '0 0 4px', fontSize: '20px' }}>PORTFOLIO WARFARE</h2>
        {teamInfo && (
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            background: `color-mix(in srgb, ${teamInfo.color} 15%, #1a1a2e)`,
            border: `1px solid ${teamInfo.color}`,
            borderRadius: '20px', padding: '4px 14px', fontSize: '13px',
          }}>
            <span>{teamInfo.piece}</span>
            <span style={{ color: teamInfo.color, fontWeight: 'bold' }}>{teamInfo.name}</span>
          </div>
        )}
        {!teamInfo && !myTeam && (
          <div style={{ color: '#666', fontSize: '12px' }}>Log in to participate</div>
        )}
      </div>

      {/* Shooter notification */}
      {isShooter && (
        <div style={{
          background: 'rgba(0,255,136,0.15)', border: '2px solid #00ff88',
          borderRadius: '12px', padding: '16px', textAlign: 'center',
          marginBottom: '16px', animation: 'blink 1s ease-in-out infinite',
        }}>
          <div style={{ fontSize: '40px', marginBottom: '8px' }}>🎯</div>
          <div style={{ color: '#00ff88', fontWeight: 'bold', fontSize: '18px' }}>YOU'RE A SHOOTER!</div>
          <div style={{ color: '#aaa', fontSize: '13px', marginTop: '4px' }}>
            You've been selected to fire for your team this round
          </div>
        </div>
      )}

      {/* Vote result */}
      {voteResult && !isShooter && (
        <div style={{
          background: voteResult.correct ? 'rgba(0,255,136,0.1)' : 'rgba(255,68,68,0.1)',
          border: `2px solid ${voteResult.correct ? '#00ff88' : '#ff4444'}`,
          borderRadius: '12px', padding: '20px', textAlign: 'center',
          marginBottom: '16px',
        }}>
          <div style={{ fontSize: '48px', marginBottom: '8px' }}>
            {voteResult.correct ? '✅' : '❌'}
          </div>
          <div style={{ color: voteResult.correct ? '#00ff88' : '#ff4444', fontWeight: 'bold', fontSize: '18px' }}>
            {voteResult.correct ? 'CORRECT!' : 'WRONG!'}
          </div>
          <div style={{ color: '#888', fontSize: '13px', marginTop: '4px' }}>
            Answer: {voteResult.answer}
          </div>
        </div>
      )}

      {/* No active game */}
      {!board && (
        <div style={{
          background: '#1a1a2e', borderRadius: '12px', padding: '40px 20px',
          textAlign: 'center', border: '1px solid #333',
        }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>⏳</div>
          <div style={{ color: '#888', fontSize: '16px' }}>Waiting for the game to start...</div>
          <Link to="/spectate/battleship" style={{
            display: 'inline-block', marginTop: '16px', padding: '8px 20px',
            background: '#222', border: '1px solid #444', borderRadius: '8px',
            color: '#aaa', textDecoration: 'none', fontSize: '13px',
          }}>Watch Spectator View</Link>
        </div>
      )}

      {/* Active mini-game */}
      {board && currentSquare && (
        <div style={{
          background: '#1a1a2e', borderRadius: '12px', padding: '20px',
          border: `2px solid ${currentSquare.color}`, marginBottom: '16px',
        }}>
          {/* Game header */}
          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <div style={{ fontSize: '48px' }}>{currentSquare.icon}</div>
            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#fff', marginTop: '4px' }}>
              {currentSquare.name}
            </div>
            <div style={{ fontSize: '14px', color: '#aaa', marginTop: '4px' }}>
              {currentSquare.prompt}
            </div>
          </div>

          {/* Voting timer */}
          {board.phase === 'voting' && (
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: '64px', height: '64px', borderRadius: '50%',
                border: '3px solid #00ff88', fontSize: '28px', fontWeight: 'bold', color: '#fff',
              }}>
                {timeLeft != null ? timeLeft : '...'}
              </div>
              <div style={{ color: '#00ff88', fontSize: '12px', marginTop: '4px' }}>
                {board.voteCount} votes
              </div>
            </div>
          )}

          {/* Vote buttons */}
          {(board.phase === 'voting' || board.phase === 'idle') && !myVote && !voteResult && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {currentSquare.options.map((opt, oi) => (
                <button
                  key={opt}
                  onClick={() => handleVote(opt)}
                  disabled={board.phase !== 'voting'}
                  style={{
                    padding: '14px',
                    background: `color-mix(in srgb, ${currentSquare.optionColors?.[oi] || '#666'} 20%, #1a1a2e)`,
                    border: `2px solid ${currentSquare.optionColors?.[oi] || '#666'}`,
                    borderRadius: '10px',
                    color: currentSquare.optionColors?.[oi] || '#ccc',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: board.phase === 'voting' ? 'pointer' : 'not-allowed',
                    opacity: board.phase === 'voting' ? 1 : 0.5,
                    transition: 'all 0.2s ease',
                  }}
                >
                  {opt}
                </button>
              ))}
            </div>
          )}

          {/* Vote submitted */}
          {myVote && !voteResult && (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>🗳️</div>
              <div style={{ color: '#00ff88', fontWeight: 'bold' }}>Vote submitted!</div>
              <div style={{ color: '#888', fontSize: '13px', marginTop: '4px' }}>
                You picked: {myVote}
              </div>
              <div style={{ color: '#666', fontSize: '12px', marginTop: '8px' }}>
                Waiting for reveal...
              </div>
            </div>
          )}

          {/* Answer revealed */}
          {board.answer && (
            <div style={{ textAlign: 'center', marginTop: '12px' }}>
              <div style={{ color: '#888', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '2px' }}>
                Answer
              </div>
              <div style={{ color: '#00ff88', fontSize: '24px', fontWeight: 'bold' }}>
                {board.answer}
              </div>
              {board.detail && (
                <div style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>
                  {board.detail.value != null && `Value: ${board.detail.value}`}
                  {board.detail.d1 != null && `Dice: ${board.detail.d1} + ${board.detail.d2} = ${board.detail.total}`}
                  {board.detail.card && `Card: ${board.detail.card}`}
                  {board.detail.first != null && `${board.detail.first} → ${board.detail.second}`}
                  {board.detail.housePick && `House played: ${board.detail.housePick}`}
                  {board.detail.handTotal != null && `Hand: ${board.detail.handTotal} + ${board.detail.nextCard} = ${board.detail.newTotal}`}
                </div>
              )}
            </div>
          )}

          {/* Team results */}
          {board.teamResults && (
            <div style={{ marginTop: '16px' }}>
              <div style={{ color: '#888', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '8px', textAlign: 'center' }}>
                Results
              </div>
              {board.turnOrder.map((team, rank) => {
                const r = board.teamResults[team];
                const info = TEAM_INFO[team];
                const advance = [4, 3, 2, 1][rank] || 1;
                return (
                  <div key={team} style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '6px 10px', background: team === myTeam ? 'rgba(255,255,255,0.05)' : 'transparent',
                    borderRadius: '6px', marginBottom: '4px',
                    border: team === myTeam ? `1px solid ${info.color}` : '1px solid transparent',
                  }}>
                    <span style={{ color: '#ffcc00', fontWeight: 'bold', width: '24px' }}>#{rank + 1}</span>
                    <span>{info.piece}</span>
                    <span style={{ color: info.color, fontWeight: 'bold', flex: 1 }}>{info.name}</span>
                    <span style={{ color: '#00ff88', fontSize: '13px' }}>{r.correct} correct</span>
                    <span style={{ color: '#ffcc00', fontSize: '12px', fontWeight: 'bold' }}>+{advance}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Idle state - no current question */}
      {board && !currentSquare && (
        <div style={{
          background: '#1a1a2e', borderRadius: '12px', padding: '30px 20px',
          textAlign: 'center', border: '1px solid #333',
        }}>
          <div style={{ fontSize: '36px', marginBottom: '8px' }}>🎲</div>
          <div style={{ color: '#aaa', fontSize: '14px' }}>Waiting for the next round...</div>
        </div>
      )}

      {/* Battleship status */}
      {battleshipState && (
        <div style={{
          background: '#111', borderRadius: '12px', padding: '12px',
          border: '1px solid #222', marginTop: '12px',
        }}>
          <div style={{ color: '#888', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px', textAlign: 'center' }}>
            Fleet Status
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
            {['red', 'blue', 'green', 'yellow'].map(team => {
              const t = battleshipState.teams?.[team];
              if (!t) return null;
              const info = TEAM_INFO[team];
              return (
                <div key={team} style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '6px 8px', background: '#1a1a2e', borderRadius: '6px',
                  border: `1px solid ${t.isEliminated ? '#333' : info.color}`,
                  opacity: t.isEliminated ? 0.4 : 1,
                }}>
                  <span>{info.piece}</span>
                  <span style={{ color: info.color, fontSize: '12px', fontWeight: 'bold' }}>{info.name}</span>
                  <span style={{ marginLeft: 'auto', color: '#888', fontSize: '11px' }}>
                    {t.shipsRemaining}/5
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default BattleshipPlayPage;
